import { ChSidebar } from "../components/ChSidebar";
import ChOptionBar from "../components/ChOptionBar";
import { CharacterForm } from "../components/CharacterForm";
import { SpEditorProvider } from "@/features/screenplay/components/editor/SpEditorProvider";
import HeadlessSpEditor from "@/features/shared/component/HeadlessSpEditor";
import { WorkspaceLayout } from "@/features/workspace";
import { Loader2, Save, Trash2, ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useCharacter } from "../queries/character.queries";
import { useRef, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { FeatureHeader } from "@/shared/components/custom/FeatureHeader";

const CharacterProviderWrapper = ({ children }: { children: React.ReactNode }) => (
    <SpEditorProvider>{children}</SpEditorProvider>
);

export default function CharacterDetailPage() {
    const { project_id, character_id } = useParams<{ project_id: string; character_id: string }>();
    const navigate = useNavigate();
    const { data: character, isLoading } = useCharacter(character_id || "");
    const formRef = useRef<{ submit: () => void; delete: () => void }>(null);
    const [isSaving, setIsSaving] = useState(false);

    if (isLoading && character_id !== "new") {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <WorkspaceLayout
            ProviderWrapper={CharacterProviderWrapper}
            SidebarComponent={<ChSidebar />}
            optionBar={<ChOptionBar />}
        >
            <HeadlessSpEditor />
            <div className="flex-1 flex flex-col h-full bg-background overflow-hidden relative">
                <FeatureHeader
                    className="p-6 pb-0"
                    title={
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/workspace/${project_id}/characters`)}
                                className="h-8 w-8 rounded-full"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            {character?.fullname || "New Character"}
                        </div>
                    }
                    subtitle="Character Profile & Variants"
                    actions={
                        <div className="flex items-center gap-2">
                            {character_id !== "new" && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => formRef.current?.delete()}
                                    className="text-muted-foreground hover:text-destructive"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                </Button>
                            )}
                            <Button
                                size="sm"
                                onClick={() => formRef.current?.submit()}
                                disabled={isSaving}
                                className="shadow-sm min-w-[120px]"
                            >
                                {isSaving ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4 mr-2" />
                                )}
                                {isSaving ? "Saving..." : "Save Changes"}
                            </Button>
                        </div>
                    }
                />
                <div className="flex-1 overflow-y-auto p-6 pt-2">
                    <div className="max-w-5xl mx-auto">
                        <CharacterForm
                            ref={formRef}
                            character={character_id === "new" ? null : character}
                            projectId={project_id || ""}
                            onClose={() => navigate(`/workspace/${project_id}/characters`)}
                            onSavingChange={setIsSaving}
                        />
                    </div>
                </div>
            </div>
        </WorkspaceLayout>
    );
}
